import {
  CheckoutDetails,
  CustomerCheckoutListItem,
  CustomerOrder,
  CustomerOrderDetails,
  OrderSummary,
  PaginatedResponse,
  TicketItemStatus,
} from '@ancon/wildcat-types'
import getT from 'next-translate/getT'
import isEmpty from 'lodash/isEmpty'

import createAppAsyncThunk from '../../../store/createAppAsyncThunk'
import api from '../../../api'
import getTranslatedError from '../../app/utils/getTranslatedError'
import { appShowError } from '../../app/store/appSlice'
import { appLanguageSelector } from '../../app/store/appSelectors'
import { defaultPaginatedResponse } from '../../outlets/constants'
import {
  clientContextCurrentUserCompanyIdSelector,
  clientContextCustomerIdSelector,
} from '../../clientContext/store/clientContextSelectors'
import { Toast } from '../../notification'
import generateActiveOrdersFilters from '../utils/generateActiveOrdersFilters'
import getTenantOutletAPIFilters from '../../app/utils/getTenantOutletAPIFilters'
import serializeError from '../../../store/utils/serializeError'

export const markOrderAsPickedUp = createAppAsyncThunk<
  void,
  { outletId: string; ticketId: string }
>(
  'orders/markOrderAsPickedUp',
  async ({ outletId, ticketId }, { dispatch, getState }) => {
    try {
      await api.core.tickets.put.byId.markAsPickedUp(undefined, {
        outletId,
        ticketId,
      })
    } catch (err) {
      const locale = appLanguageSelector(getState())
      const t = await getT(locale, 'common')
      const error = await getTranslatedError(
        err,
        {
          title: t('errors.markAsPickedUpFailure.title'),
          message: t('errors.markAsPickedUpFailure.message'),
        },
        locale,
      )

      dispatch(appShowError(error))
    }
  },
)

export const markTicketItemAsPickedUp = createAppAsyncThunk<
  void,
  { outletId: string; ticketId: string; ticketItemIds: string[] }
>(
  'orders/markTicketItemAsPickedUp',
  async ({ outletId, ticketId, ticketItemIds }, { dispatch, getState }) => {
    try {
      await api.core.tickets.put.byId.ticketItemsStatus(
        {
          ticketItemIds,
          ticketItemStatus: TicketItemStatus.Closed,
        },
        {
          outletId,
          ticketId,
        },
      )
    } catch (err) {
      const locale = appLanguageSelector(getState())
      const t = await getT(locale, 'common')
      const error = await getTranslatedError(
        err,
        {
          title: t('errors.markAsPickedUpFailure.title'),
          message: t('errors.markAsPickedUpFailure.message'),
        },
        locale,
      )

      dispatch(appShowError(error))
    }
  },
)

export const sendOrderReceipt = createAppAsyncThunk<
  void,
  { orderId: string; email: string }
>('orders/sendOrderReceipt', async ({ orderId, email }, { getState }) => {
  const locale = appLanguageSelector(getState())
  const t = await getT(locale, 'common')
  try {
    await api.core.orderReceipt.post.send({ email }, { orderId })
    Toast.success(
      t('components.toasts.receiptSent.title'),
      t('components.toasts.receiptSent.message', { email }),
    )
  } catch (err) {
    Toast.error(
      t('components.toasts.receiptSentError.title'),
      t('components.toasts.receiptSentError.message', { email }),
    )
    throw err
  }
})

export const downloadOrderReceipt = createAppAsyncThunk<Blob, string>(
  'orders/downloadOrderReceipt',
  async orderId => {
    const response = await api.core.orderReceipt.get.download(
      { orderId },
      undefined,
      { responseType: 'blob' },
    )

    return response.data
  },
)

export const fetchOngoingOrders = createAppAsyncThunk<
  PaginatedResponse<CheckoutDetails>,
  | {
      isBackgroundFetch?: boolean
    }
  | undefined
>('orders/fetchOngoingOrders', async (args, { getState }) => {
  const customerId = clientContextCustomerIdSelector(getState())
  const companyId = clientContextCurrentUserCompanyIdSelector(getState())

  const filters = generateActiveOrdersFilters()

  if (customerId) {
    const response = await api.core.checkouts.get.checkouts({
      offset: 0,
      companyId,
      filter: filters,
    })
    return response.data
  }

  return defaultPaginatedResponse
})

export const fetchUpcomingOrders = createAppAsyncThunk<
  PaginatedResponse<CustomerCheckoutListItem>
>('orders/fetchUpcomingOrders', async (_, { getState }) => {
  const customerId = clientContextCustomerIdSelector(getState())
  const companyId = clientContextCurrentUserCompanyIdSelector(getState())
  const filters = generateActiveOrdersFilters(true)

  if (customerId) {
    const response = await api.core.checkouts.get.checkoutsV2({
      offset: 0,
      companyId,
      filter: filters,
    })
    return response.data
  }

  return defaultPaginatedResponse
})

export const fetchOrdersList = createAppAsyncThunk<
  PaginatedResponse<CustomerOrder>
>('orders/fetchOrdersList', async (_, { getState }) => {
  const tenantOutletFilters = getTenantOutletAPIFilters({
    tenantFilterProp: 'tenant.id',
  })

  const companyId = clientContextCurrentUserCompanyIdSelector(getState())

  const response = await api.core.orders.get.list({
    order: 'createdAt:desc',
    offset: 0,
    companyId,
    ...(!isEmpty(tenantOutletFilters) && {
      filter: tenantOutletFilters,
    }),
  })
  return response.data
})

export const fetchOrderDetails = createAppAsyncThunk<
  CustomerOrderDetails,
  string
>('orders/fetchOrderDetails', async orderId => {
  const response = await api.core.orders.get.details({ orderId })
  return response.data
})

export const fetchOrderSummary = createAppAsyncThunk<OrderSummary, string>(
  'orders/fetchOrderSummary',
  async orderId => {
    const response = await api.core.order.get.orderStatus({
      orderId,
    })

    return response.data?.orderSummary
  },
)

export const fetchOrderTicketItemsStatus = createAppAsyncThunk<
  {
    timestamp: string
    statuses: Record<string, TicketItemStatus>
  },
  {
    correlationId?: string
    outletId: string
    orderId: string
    ticketItemIds: string[]
  }
>(
  'check/fetchOrderTicketItemsStatus',
  async ({ correlationId, outletId, ticketItemIds }) => {
    const ticketItemIdsQuery = ticketItemIds.reduce<Record<string, string>>(
      (acc, id, index) => {
        acc[`ticketItemIds[${index}]`] = id
        return acc
      },
      {},
    )

    const response = await api.core.order.get.ticketItemsStatus(
      {
        outletId,
        ...ticketItemIdsQuery,
      },
      {
        'x-correlation-id': correlationId,
      },
    )

    return response.data
  },
  { serializeError },
)
